'use client';

import { Eye, Heart, MessageCircle, User } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { useRef } from 'react';

import { FeatherIcon } from '@/components/icons';
import { typeOptions } from '@/data/article-types';
import { MediaAuthorNames } from '@/features/media-authors/components/media-author-names';
import type { PopulatedMediaAuthor } from '@/features/media-authors/types';
// NOTE: Luxon removed for smaller client bundle/TBT.
// If you need to revert, uncomment the next line and the `DateTime...` usage below.
// import { DateTime } from 'luxon'
import { cn } from '@/lib/utils';
import { trackArticleClick } from '@/lib/analytics';
import { useArticleViewTracker } from '@/hooks/use-article-view-tracker';
import { MEDIA_AUTHORS_INTERACTIVE_CARD_TITLE_CLASS } from '@/features/media-authors/styles/media-authors-card-shadow';

export interface Article {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  author: string;
  date: string;
  heroImage: {
    id: number;
    alt: string | null;
    caption: string | null;
    updatedAt: string;
    createdAt: string;
    url: string;
    thumbnailURL: string;
    filename: string;
    mimeType: string;
    filesize: number;
    width: number;
    height: number;
    focalX: number;
    focalY: number;
    sizes: {
      thumbnail: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
      square: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
      small: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
      medium: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
      large: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
      xlarge: {
        url: null;
        width: null;
        height: null;
        mimeType: null;
        filesize: null;
        filename: null;
      };
      og: {
        url: string;
        width: number;
        height: number;
        mimeType: string;
        filesize: number;
        filename: string;
      };
    };
  };
  likes?: number;
  comments?: number;
  type?: string;
  views?: number;
  slideOrder?: number | null;
  main?: boolean;
  populatedAuthors: PopulatedMediaAuthor[];
  fandoms: {
    id: number;
    name: string;
    universe: {
      id: number;
      name: string;
      description: string | null;
      updatedAt: string;
      createdAt: string;
    };
    description: string | null;
    updatedAt: string;
    createdAt: string;
  }[];
  publishedAt: string;
  tags: {
    name: string;
    id: number;
  }[];
}

interface ArticleCardProps {
  article: Article;
  size?: 'small' | 'medium' | 'large';
  showStats?: boolean;
  noImage?: boolean;
  target?: string;
  cardClassName?: string;
}

export default function ArticleCard({
  article,
  size = 'medium',
  showStats = false,
  noImage = false,
  target,
  cardClassName,
}: ArticleCardProps) {
  const cardRef = useRef<HTMLElement>(null);

  const type = typeOptions.find((option) => option.value === article.type)?.label || '';

  // Track preview view using shared IntersectionObserver (reduces TBT)
  useArticleViewTracker(cardRef, article.id);

  const handleClick = (e: React.MouseEvent) => {
    // Track article click only on regular clicks (not Ctrl+Click)
    if (!e.ctrlKey && !e.metaKey) {
      trackArticleClick(article.id);
    }
  };

  const sizeClasses = {
    small: 'h-64',
    medium: 'h-80',
    large: 'h-80',
  };

  const titleClasses = {
    small: 'text-lg md:text-2xl',
    medium: 'text-xl md:text-article-title',
    large: 'text-xl md:text-article-title',
  };

  const imageUrl = article?.heroImage?.url || '/placeholder.svg';
  const heroSizes = article?.heroImage?.sizes;

  const publishedAtLabel = (() => {
    try {
      // dd.MM.yyyy (как было в Luxon)
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(article.publishedAt));
    } catch {
      return article.publishedAt;
    }
  })();

  // Подбираем исходник и sizes под реальные размеры карточек на /media:
  // - desktop: small в 3 колонки ≈ 270-300px, medium в 2 колонки ≈ 400-430px
  // - mobile: карточка почти на всю ширину экрана
  const cardImageSrc =
    size === 'small'
      ? (heroSizes?.small?.url ?? heroSizes?.medium?.url ?? imageUrl)
      : (heroSizes?.medium?.url ?? heroSizes?.large?.url ?? imageUrl);

  const cardImageWidth =
    size === 'small'
      ? (heroSizes?.small?.width ?? heroSizes?.medium?.width ?? 640)
      : (heroSizes?.medium?.width ?? heroSizes?.large?.width ?? 860);

  const cardImageHeight =
    size === 'small'
      ? (heroSizes?.small?.height ?? heroSizes?.medium?.height ?? 360)
      : (heroSizes?.medium?.height ?? heroSizes?.large?.height ?? 480);

  const cardImageSizes =
    size === 'small' ? '(max-width: 768px) 100vw, 300px' : '(max-width: 768px) 100vw, 430px';

  const cardImageQuality = size === 'small' ? 75 : 80;

  const articleHref =
    article.type === 'quiz' ? `/quiz/${article.slug}` : `/article/${article.slug}`;
  const primaryAuthor = article.populatedAuthors?.[0];
  const primaryAuthorAvatar = primaryAuthor?.avatar ?? undefined;

  const isInteractiveCard = Boolean(cardClassName);

  return (
    <article
      ref={cardRef}
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-2xl bg-card dark:bg-[#26282D]',
        cardClassName,
      )}
    >
      <Link
        href={articleHref}
        onClick={handleClick}
        className="flex min-h-0 flex-1 flex-col"
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      >
        {/* Article Image */}
        {!noImage && (
          <div className={`relative md:${sizeClasses[size]} overflow-hidden p-3 pb-0`}>
            <div className="max-h-[175px]">
              <Image
                loading="lazy"
                src={cardImageSrc}
                alt={article.title}
                className="w-full h-[175px] object-cover rounded-2xl"
                width={cardImageWidth}
                height={cardImageHeight}
                quality={cardImageQuality}
                sizes={cardImageSizes}
              />
              {/* Tags and Author Info */}
              <div className="flex items-center mb-3 space-x-2 absolute md:-bottom-2.5 bottom-0 left-[18px] right-2">
                {type && (
                  <span className="flex items-center justify-between gap-2 bg-[#F7F7F7] p-3 py-2 rounded-lg text-badge text-[#1B1D22] font-bold">
                    <FeatherIcon fill="#1B1D22" className="w-4 h-4 text-[#1B1D22]" />
                    {type}
                  </span>
                )}
                {/* {
              article?.tags?.map((tag: any) => (
                <span key={tag.id} className="flex items-center justify-between gap-2 bg-[#F7F7F7] text-[#898989] p-3 py-2 rounded-xl text-badge font-bold">
                  {tag.name}
                </span>
              ))
            }
            {
              article?.fandoms?.map((fandom: any) => (
                <span key={fandom.id} className="flex items-center justify-between gap-2 bg-[#F7F7F7] text-[#898989] p-3 py-2 rounded-xl text-badge font-bold">
                  {fandom.name}
                </span>
              ))
            } */}
                {/* <div className="flex items-center space-x-2 text-[#898989]">
            <div className="w-6 h-6 bg-[#898989] rounded-full flex items-center justify-center">
              <User className="w-3 h-3 text-white" />
            </div>
            <span className="text-badge font-bold text-[#898989]">{article.category}</span>
            <MoreHorizontal className="w-4 h-4" />
          </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Article Content */}
        <div
          className={cn(
            `${size === 'small' ? 'px-3' : 'px-4 md:px-6'} pt-3 flex flex-col flex-1`,
            noImage && 'sm:min-h-[300px] h-full flex-1',
          )}
        >
          {type && noImage && (
            <span className="w-20 flex items-center justify-center mb-3 gap-2 bg-[#F7F7F7] py-1 px-2 rounded-lg text-badge text-[#1B1D22] text-[12px]">
              <FeatherIcon fill="#1B1D22" className="w-[14px] h-[14px] text-[#1B1D22]" />
              {type}
            </span>
          )}
          <div className="flex flex-col justify-between h-full flex-1">
            <div>
              {/* Article Title (styled text instead of heading tag for SEO) */}
              <div
                className={cn(
                  `${titleClasses[size]} mb-3 font-black uppercase leading-tight text-card-foreground dark:text-white`,
                  noImage && 'break-words',
                  isInteractiveCard && MEDIA_AUTHORS_INTERACTIVE_CARD_TITLE_CLASS,
                )}
                style={{
                  fontWeight: 900,
                  fontStyle: 'normal',
                  fontSize: '19px',
                  lineHeight: '115%',
                  letterSpacing: '0%',
                  textTransform: 'uppercase',
                }}
              >
                {article.title}
              </div>

              {/* Article Description */}
              <p
                className={`text-[12px] font-medium text-muted-foreground dark:text-[#898989] mb-5 leading-[140%]`}
              >
                {article.description}
              </p>
            </div>
          </div>
        </div>
      </Link>
      <div className={size === 'small' ? 'px-3 pb-3' : 'px-4 pb-4 md:px-6 md:pb-6'}>
        <div>
          {/* Author Info */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 min-w-0">
              <div className="flex items-center space-x-2 min-w-0">
                {primaryAuthorAvatar ? (
                  <Image
                    src={primaryAuthorAvatar}
                    alt={primaryAuthor?.name || 'Molwe'}
                    className="w-5 h-5 rounded-full object-cover shrink-0"
                    width={20}
                    height={20}
                  />
                ) : (
                  <div className="w-5 h-5 bg-[#898989] rounded-full flex items-center justify-center shrink-0">
                    <User className="w-3 h-3 text-white" />
                  </div>
                )}
                <MediaAuthorNames
                  authors={article.populatedAuthors}
                  className="text-[12px] font-medium text-card-foreground dark:text-white truncate"
                />
              </div>
              <div className="text-[12px] font-normal text-[#898989] whitespace-nowrap shrink-0">
                {/* Luxon version (revert if needed): */}
                {/* {DateTime.fromISO(article.publishedAt).toFormat('dd.MM.yyyy')} */}
                {publishedAtLabel}
              </div>
            </div>

            {/* Stats */}
            {showStats && (
              <div className="flex items-center space-x-4 text-[#898989]">
                <div className="flex items-center space-x-1">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm">{article.likes || 3}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm">{article.comments || 7}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">{article.views || 293}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
